import { PushpinFilled } from "@ant-design/icons";
import { Button, Divider, Flex, Input, Popconfirm, Progress, Space, Spin, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { GetData, MutateData } from "../handlers/axios";
import { BookId, UserId } from "../handlers/globals";
import { Defaults, QueryCase, QueryId, WebRoutes } from "../handlers/enums";
import ComponentBookIcon from "./components/bookicon";
import ComponentState from "./components/states";

export function SectionSearchUserDone(props) {
    const { t } = useTranslation();

    const [userId] = useAtom(UserId);

    const responseUser = GetData(QueryId.SelectUserLite, { UserId: userId });
    const response = GetData(QueryId.SelectUserBooks, { UserId: userId, Mode: props.mode });

    const [searchInput, setSearchInput] = useState(Defaults.StringEmpty);

    if (responseUser.isLoading || response.isLoading) {
        return <Spin size="large" className="mx-auto mt-8 block" />;
    }

    if (responseUser.isFalse || response.isFalse) {
        return <ComponentState error />;
    }

    if (response.isEmpty) {
        return <ComponentState empty />;
    }

    const filteredBooks = response.data.filter((book) => {
        const title = (book.title ?? Defaults.StringEmpty).toLowerCase();
        const authors = (book.authors ?? Defaults.StringEmpty).toLowerCase();
        const query = searchInput.toLowerCase();
        return title.includes(query) || authors.includes(query);
    });

    const yearlyGoal = responseUser.data.yearlyBookGoal;
    const progressPercent = Number((yearlyGoal === 0 ? 100 : response.data.length / yearlyGoal * 100).toFixed(0));

    return (
        <Space direction="vertical" size={12} className="w-full">
            <div className="rounded-xl border border-white/10 bg-custom-darkgrey/40 p-3">
                <Typography.Text className="!text-custom-white">
                    {t("yearly_goal")} <span className="font-semibold">{yearlyGoal}</span>
                    <span className="ml-2 text-custom-white/80">({response.data.length}/{yearlyGoal === 0 ? "∞" : yearlyGoal})</span>
                </Typography.Text>
                <Progress
                    className="mb-1 mt-2"
                    trailColor="#ff0000"
                    strokeColor="#00aa00"
                    percent={progressPercent}
                    status={response.data.length === yearlyGoal || yearlyGoal === 0 ? undefined : "active"}
                />
            </div>

            <Input className="app-input custom-color-placeholder" size="large" onChange={(value) => setSearchInput(value.target.value)} placeholder={t("placeholder_searchbooks")} allowClear />
            <Divider className="!my-1 !border-custom-darkgrey" />

            {filteredBooks.length === 0 ? <ComponentState empty /> : filteredBooks.map((book) => <Book key={book.id} book={book} />)}
        </Space>
    );
}

function Book(props) {
    const { t } = useTranslation();

    const [userId] = useAtom(UserId);
    const [, setBookId] = useAtom(BookId);

    const [freezeActions, setFreezeActions] = useState(false);
    const [hidden, setHidden] = useState(false);

    const navigate = useNavigate();

    const actionDelete = MutateData(QueryId.ToggleUserBook, () => {
        setFreezeActions(false);
    }, [
        { case: QueryCase.Success, onCase: () => setHidden(true) }
    ]);

    const onDelete = () => {
        setFreezeActions(true);
        actionDelete.mutate({
            UserId: userId,
            BookId: props.book.id
        });
    };

    if (hidden) {
        return null;
    }

    return (
        <div className="rounded-2xl border border-white/10 bg-custom-darkgrey/40 p-3">
            <Flex gap={16} align="start">
                <div className="relative">
                    <Popconfirm
                        title={t("confirm_delete_book_title")}
                        description={t("confirm_delete_book_description")}
                        onConfirm={onDelete}
                        okText={t("yes")}
                        cancelText={t("no")}
                        okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                        cancelButtonProps={{ className: "hover:!text-custom-white" }}
                    >
                        <Button disabled={freezeActions} className="absolute left-[84px] top-1 z-10 !border-custom-red !bg-custom-red disabled:!bg-custom-red active:!bg-custom-red" icon={<PushpinFilled />} size="large" />
                    </Popconfirm>
                    <ComponentBookIcon book={props.book} />
                </div>
                <div className="flex-1 text-custom-white">
                    <Typography.Text className="!text-custom-white">{props.book.title}</Typography.Text>
                    <Typography.Paragraph className="!mb-1 !text-custom-white/90">{String(props.book.authors ?? Defaults.StringEmpty).replace(/,\s*/g, ", ")}</Typography.Paragraph>
                    <Typography.Text className="!mb-3 !block !text-custom-white/75">{t("page_amount")} {props.book.pageCount === 0 ? t("unknown") : props.book.pageCount}</Typography.Text>
                    <Button
                        disabled={freezeActions}
                        className="min-w-[128px] bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]"
                        onClick={() => {
                            setBookId(props.book.id);
                            navigate(WebRoutes.Book);
                        }}
                        size="large"
                    >
                        {t("view_book")}
                    </Button>
                </div>
            </Flex>
        </div>
    );
}
